import React, { useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../../util/reactIntl';
import {
  DATE_MODE_QUERY_PARAM,
  formatDateContext,
  parseDateContext,
} from '../../../../util/searchDates';
import { OutsideClickHandler } from '../../../../components';

// Reaching across into SearchPage is deliberate: DayOrMonthPicker was built as a standalone
// component so it could be lifted out of the sidebar filter, and it takes only value/onChange
// with no form context. Rebuilding the calendar here would give the header and the sidebar two
// pickers to keep in step.
import DayOrMonthPicker from '../../../SearchPage/BookingDateRangeFilter/DayOrMonthPicker';

import css from './SearchDateSegment.module.css';

const KEY_CODE_ESCAPE = 27;

/**
 * The "Wann" half of the topbar search: a button showing the chosen date, opening the
 * day/month picker.
 *
 * Reads and writes the same `dates` / `dateMode` pair the sidebar filter does, through
 * util/searchDates — the two controls are two doors into one query, so neither owns the
 * format. Selecting closes the popover and lets the form submit, the way choosing a place
 * in the location field already does: a date on its own is a complete search.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.dates current `dates` query param, e.g. '2027-06-14,2027-06-14'
 * @param {string?} props.dateMode current `dateMode` query param, 'month' or null
 * @param {Function} props.onChange called with { dates, dateMode }, both null to clear
 * @returns {JSX.Element} date segment
 */
const SearchDateSegment = props => {
  const { className, dates, dateMode, onChange } = props;
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const label = formatDateContext(parseDateContext(dates, dateMode), intl);

  // An aria-label replaces the visible text as the accessible name, so a bare "Wann" would hide
  // the chosen date from a screen reader entirely - it needs to carry the value as well as the
  // purpose. Nothing to add when there is no date: the two lines already read "Wann" and the
  // placeholder.
  const ariaLabel = label
    ? intl.formatMessage({ id: 'TopbarSearchForm.dateLabelSelected' }, { date: label })
    : undefined;

  return (
    <OutsideClickHandler
      className={classNames(css.root, className)}
      onOutsideClick={() => setIsOpen(false)}
    >
      <button
        type="button"
        className={css.trigger}
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={() => setIsOpen(open => !open)}
        onKeyDown={e => {
          if (e.keyCode === KEY_CODE_ESCAPE) {
            setIsOpen(false);
          }
        }}
      >
        <span className={css.triggerLabel}>
          {intl.formatMessage({ id: 'TopbarSearchForm.dateLabel' })}
        </span>
        <span className={classNames(css.triggerValue, { [css.triggerValueSet]: !!label })}>
          {label || intl.formatMessage({ id: 'TopbarSearchForm.datePlaceholder' })}
        </span>
      </button>

      {isOpen ? (
        <div
          className={css.popover}
          onKeyDown={e => {
            if (e.keyCode === KEY_CODE_ESCAPE) {
              setIsOpen(false);
            }
          }}
        >
          <DayOrMonthPicker
            value={dates}
            dateMode={dateMode}
            onChange={next => {
              setIsOpen(false);
              onChange({
                dates: next.dates,
                [DATE_MODE_QUERY_PARAM]: next[DATE_MODE_QUERY_PARAM],
              });
            }}
          />
        </div>
      ) : null}
    </OutsideClickHandler>
  );
};

export default SearchDateSegment;
